import {
  InputGroup,
  InputRow,
  errorsToPropertyErrors,
  ModelNewNumericInput,
  Flex,
  EmptyInput,
  Table,
  TripleToggleInput,
  ModelSelect,
  ModelInput,
  useDeleteAlert,
  flattenErrorTypeArray,
  IconButton,
} from '@equifinance/shared';

import styled from 'styled-components';
import { useCallback } from 'react';
import { stageOnOrAfter } from '../../../../contexts/helpers';
import { PaddedSection } from '../../components/Section';
import { TableStatusIcon } from '../../Icons/TableStatusIcon';
import { Stages } from '../../../../types/Stages';
import { DeleteIcon, PlusIcon } from '../../components/Icons';
import { SectionTitleIcon } from '../../Icons/SectionTitleIcon';
import {
  OtherOccupant, Relationship, Source, useAccountContext,
} from '../../../../contexts';
import useErrorBasedRowStyles from '../../../../hooks/useErrorBasedRowStyles';
import { usePageConfigurationContext } from '../../../../contexts/pageConfiguration';
import { useProgressStatusByInitiator } from '../../../../contexts/progressStatus/ProgressStatusProvider';

export const householdDependentsRefName = 'householdDependents';

const Banner = styled(Flex)`
    justify-content: space-between;
    align-items: center;
    height:32px;
`;

const BannerStart = styled(Flex)`${({ theme }) => `
    flex:1;
    align-items: center;
    background: ${theme.colours.primary[3]};
    color: ${theme.colours.neutral[10]};
    padding-left: ${theme.padding[4]};
    height:100%;
`}`;

const BannerEnd = styled(Flex) <{ disabled?: boolean }>`${({ theme, disabled }) => `
    flex:1;
    align-items: center;
    justify-content:end;
    background: ${theme.colours.primary[8]};
    color: ${theme.colours.primary[1]};
    padding-right: ${theme.padding[4]};
    cursor:  ${disabled ? 'not-allowed' : 'pointer'};
    height:100%;
    font-size: ${theme.fontSize[1]};
    font-weight: ${theme.fontWeight.bolder};
`}`;

const InputFlex = styled(Flex)`${({ theme }) => `
  align-items: center;
  gap: ${theme.padding[4]};
  padding-bottom: ${theme.padding[8]};
`}`;

const DeleteOver18Container = styled(Flex)`${({ theme }) => `
  justify-content: end;
  width:100%;
  padding-right: ${theme.padding[5]};
`}`;

const DeleteUnder18 = styled(IconButton)`${({ theme }) => `
  margin-top: ${theme.padding[6]}
`}`;

export default function HouseholdDependentsSection() {
  const {
    account,
    appendToAccountArray,
    deleteFromAccountArray,
    updateAccount,
  } = useAccountContext();

  const {
    stage,
    householdCompositions,
    errors,
    warnings,
  } = account;

  const { isReadOnly } = usePageConfigurationContext();

  const getOccupantPropertyErrors = (occupantId: string) => errorsToPropertyErrors(errors?.[occupantId]);

  const householdCompositionErrors = errors?.[householdCompositions?.id] ?? [];

  const listChildrenUnder18Errors = householdCompositions?.listChildrenUnder18?.map(
    (childUnder18) => errors?.[childUnder18?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const otherOccupantsErrors = householdCompositions?.otherOccupants?.map(
    (otherOccupant) => errors?.[otherOccupant?.id],
  )?.flat()?.filter((v) => !!v) ?? [];

  const combinedErrors = [
    ...householdCompositionErrors,
    ...listChildrenUnder18Errors,
    ...otherOccupantsErrors,
  ];

  const conditionalRowStyles = useErrorBasedRowStyles(errors, warnings);

  const addUnder18InProgress = useProgressStatusByInitiator('addUnder18-button');

  const addUnder18 = () => {
    if (isReadOnly || addUnder18InProgress) return;
    appendToAccountArray('householdCompositions.listChildrenUnder18', {}, 'addUnder18-button');
  };

  const deleteAlert = useDeleteAlert();

  const deleteUnder18 = (id: string) => {
    deleteAlert({
      onConfirm: () => deleteFromAccountArray('householdCompositions.listChildrenUnder18', id),
      text: 'Are you sure you want to delete this Dependent?',
    });
  };

  const addOver18InProgress = useProgressStatusByInitiator('addOver18-button');

  const addOver18 = () => {
    if (isReadOnly || addOver18InProgress) return;

    appendToAccountArray('householdCompositions.otherOccupants', {
      source: 'Declared',
      currentlyLivingInTheProperty: true,
    } as OtherOccupant, 'addOver18-button');
  };

  const deleteOver18 = (id: string) => {
    deleteAlert({
      onConfirm: () => deleteFromAccountArray('householdCompositions.otherOccupants', id),
      text: 'Are you sure you want to delete this record?',
    });
  };

  const Status = useCallback((otherOccupant: OtherOccupant) => (
    <TableStatusIcon
      recordErrors={flattenErrorTypeArray(errors[otherOccupant?.id]) ?? []}
      recordWarnings={flattenErrorTypeArray(warnings[otherOccupant?.id]) ?? []}
      errors={flattenErrorTypeArray(errors[otherOccupant?.id])}
      showDefault={!otherOccupant?.id}
    />
  ), [householdCompositions]);

  const Name = useCallback((otherOccupant: OtherOccupant) => {
    const index = householdCompositions?.otherOccupants.map((e) => e.id).indexOf(otherOccupant?.id);

    if (otherOccupant?.source === 'ElectoralRegister') {
      return otherOccupant?.name;
    }

    return (
      <ModelInput
        field={`householdCompositions.otherOccupants[${index}].name`}
        mutateValue={(v) => v.replace(/[^a-z'-\s]/gmi, '')}
        isTooltipError
      />
    );
  }, [householdCompositions]);

  const LivingInProperty = useCallback((otherOccupant: OtherOccupant) => {
    const index = householdCompositions?.otherOccupants.map((e) => e.id).indexOf(otherOccupant?.id);

    return (
      <>
        {otherOccupant?.source !== 'Declared' && (
          <TripleToggleInput
            id={`living-in-porperty-${otherOccupant?.id}`}
            value={householdCompositions?.otherOccupants[index]?.currentlyLivingInTheProperty}
            onChange={(value?: boolean) => updateAccount(`householdCompositions.otherOccupants[${index}].currentlyLivingInTheProperty`, value)}
            errors={getOccupantPropertyErrors(otherOccupant.id)?.currentlyLivingInTheProperty}
          />
        )}
      </>

    );
  }, [householdCompositions]);

  const RelationshipSelect = useCallback((otherOccupant: OtherOccupant) => {
    const index = householdCompositions?.otherOccupants.map((e) => e.id).indexOf(otherOccupant?.id);

    return (
      <ModelSelect
        field={`householdCompositions.otherOccupants[${index}].relationship`}
        options={Relationship}
        disabled={!otherOccupant?.currentlyLivingInTheProperty}
        isTooltipError
      />
    );
  }, [householdCompositions]);

  const DependentToggle = useCallback((otherOccupant: OtherOccupant) => {
    const index = householdCompositions?.otherOccupants.map((e) => e.id).indexOf(otherOccupant?.id);

    return (
      <TripleToggleInput
        id={`dependent-${otherOccupant?.id}`}
        value={householdCompositions?.otherOccupants[index]?.dependent}
        onChange={(value?: boolean) => updateAccount(`householdCompositions.otherOccupants[${index}].dependent`, value)}
        disabled={!otherOccupant?.currentlyLivingInTheProperty}
        errors={getOccupantPropertyErrors(otherOccupant.id)?.dependent}
      />
    );
  }, [householdCompositions]);

  const DeleteOver18 = useCallback((otherOccupant: OtherOccupant) => (
    <DeleteOver18Container>
      {otherOccupant?.source === 'Declared' && (
        <IconButton onClick={() => deleteOver18(otherOccupant?.id)}>
          <DeleteIcon />
        </IconButton>
      )}
    </DeleteOver18Container>
  ), [householdCompositions]);

  const columns = [
    {
      name: 'Status',
      sortable: true,
      cell: (otherOccupant: OtherOccupant) => Status(otherOccupant),
      width: '70px',
    },
    {
      name: 'Name and surname',
      sortable: true,
      cell: (otherOccupant: OtherOccupant) => Name(otherOccupant),
    },
    {
      name: 'Source',
      sortable: true,
      selector: (otherOccupant: OtherOccupant) => Source?.[otherOccupant?.source as keyof typeof Source] ?? '',
    },
    {
      name: 'Currently living in the property',
      cell: (otherOccupant: OtherOccupant) => LivingInProperty(otherOccupant),
      width: '90px',
    },
    {
      name: 'Relationship',
      cell: (otherOccupant: OtherOccupant) => RelationshipSelect(otherOccupant),
    },
    {
      name: 'Dependent',
      cell: (otherOccupant: OtherOccupant) => DependentToggle(otherOccupant),
      width: '90px',
    },
    {
      name: '',
      cell: (otherOccupant: OtherOccupant) => DeleteOver18(otherOccupant),
    },
  ];

  return (
    <PaddedSection
      id={householdCompositions?.id}
      refName={householdDependentsRefName}
      title="Household Dependents"
      titleIcon={<SectionTitleIcon errors={flattenErrorTypeArray(combinedErrors)} />}
      errors={errors}
      warnings={warnings}
    >
      {stage === Stages.Affordability && (
        <InputGroup>
          <InputRow>
            <ModelNewNumericInput
              label="No. of dependents under the age of 18"
              field="householdCompositions.numberOfDependentsUnder18"
              required
              integer
              noGroupingSeperator
            />
            <ModelNewNumericInput
              label="No. of dependents aged 18 and over"
              field="householdCompositions.numberOfDependentsAbove18"
              required
              tooltip="You will require waivers for each dependent."
              integer
              noGroupingSeperator
            />
          </InputRow>
        </InputGroup>
      )}

      {stageOnOrAfter(stage, Stages.Packaging) && (
        <>
          <Banner>
            <BannerStart>
              Dependents under the age of 18
            </BannerStart>
            {!isReadOnly && (
              <BannerEnd disabled={isReadOnly || addUnder18InProgress} onClick={addUnder18}>
                <PlusIcon />
                Add
              </BannerEnd>
            )}
          </Banner>

          <InputGroup>
            {householdCompositions?.listChildrenUnder18?.map((under18s, index) => (

              <InputFlex key={under18s?.id}>
                <ModelNewNumericInput
                  label={`Dependent ${index + 1} - age`}
                  field={`householdCompositions.listChildrenUnder18[${index}].age`}
                  required
                  integer
                  max={17}
                  suffix="years"
                />
                <DeleteUnder18 onClick={() => deleteUnder18(under18s?.id)}>
                  <DeleteIcon />
                </DeleteUnder18>
                <EmptyInput />
              </InputFlex>
            ))}
          </InputGroup>

          <Banner>
            <BannerStart>
              Dependents over the age of 18 and non-dependent occupants
            </BannerStart>
            {!isReadOnly && (
              <BannerEnd disabled={isReadOnly || addOver18InProgress} onClick={addOver18}>
                <PlusIcon />
                Add
              </BannerEnd>
            )}
          </Banner>

          <Table
            columns={columns}
            data={householdCompositions?.otherOccupants ?? []}
            showSearch={false}
            conditionalRowStyles={conditionalRowStyles}
          />
        </>
      )}
    </PaddedSection>
  );
}
